/**
 * Custom fetch wrapper for Orval-generated clients.
 * Integrates CSRF token injection and session credentials.
 */
import * as Sentry from '@sentry/react';

// CSRF token getter - injected by initializeAPIs()
let getCsrfToken: (() => Promise<string>) | null = null;

export const setCsrfTokenGetter = (tokenGetter: () => Promise<string>) => {
  getCsrfToken = tokenGetter;
};

const API_BASE_URL = import.meta.env.VITE_API_URL || (import.meta.env.DEV ? 'http://localhost:8000' : '');

export const customFetch = async <T>(
  url: string,
  options: RequestInit
): Promise<T> => {
  // Get CSRF token if available
  const csrfToken = getCsrfToken ? await getCsrfToken() : '';

  // Build headers
  const headers: Record<string, string> = {
    ...((options.headers || {}) as Record<string, string>),
  };

  // Add CSRF token for mutations (POST, PUT, PATCH, DELETE)
  const method = options.method?.toUpperCase() || 'GET';
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method) && csrfToken) {
    headers['X-CSRFToken'] = csrfToken;
  }

  // Don't override Content-Type if it's already set (for FormData)
  if (!headers['Content-Type'] && options.body && typeof options.body === 'string') {
    headers['Content-Type'] = 'application/json';
  }

  // Make request with session credentials
  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    credentials: 'include',
    headers,
  });

  // Handle error responses
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));

    // DRF-compatible error handling
    if (response.status === 400) {
      // Field validation errors: { field_name: ["error message"] }
      const fieldErrors = Object.entries(errorData)
        .map(([field, errors]) => `${field}: ${errors}`)
        .join(', ');
      throw new Error(fieldErrors || 'Validation error');
    }

    if (response.status === 403) {
      const error = new Error(errorData.detail || errorData.error || 'Forbidden') as Error & { code?: string; status?: number };
      error.code = errorData.code;
      error.status = 403;
      throw error;
    }

    const errorMessage =
      errorData.detail ||
      errorData.error ||
      errorData.message ||
      `HTTP error! status: ${response.status}`;

    const error = new Error(errorMessage);

    // Capture 5xx errors to Sentry
    if (response.status >= 500) {
      Sentry.captureException(error, {
        extra: { url, method, status: response.status, errorData },
        tags: { type: 'api_error', endpoint: url },
      });
    }

    throw error;
  }

  // Handle 204 No Content responses
  if (response.status === 204) {
    return { data: {}, status: 204 } as T;
  }

  // Wrap response to match Orval's expected structure: { data: T, status: number }
  const data = await response.json();
  return { data, status: response.status } as T;
};
